import React, { useState, useEffect } from 'react';
import {
  MantineProvider,
  Container,
  Paper,
  Tabs,
  Title,
  Text,
  Stack,
  Group,
  Loader,
  Switch,
  Button,
  useMantineColorScheme,
} from '@mantine/core';
import { createStyles } from '@mantine/styles';
import {
  IconCamera,
  IconUpload,
  IconBug,
  IconArrowBack,
  IconSun,
  IconMoon,
} from '@tabler/icons-react';
import WebcamCapture from './components/WebcamCapture';
import ImageUpload from './components/ImageUpload';
import ResultDisplay from './components/ResultDisplay';
import Navbar from './components/Navbar';
import ModelSelector from './components/ModelSelector';
import TestModel from './components/TestModel';
import {
  loadModel,
  predictAgeGender,
  clearAllModels,
} from './services/modelService';
import './App.css';

const useStyles = createStyles((theme) => ({
  mainContainer: {
    padding: '40px 16px',
    minHeight: 'calc(100vh - 70px)',
    background: 'var(--bg-gradient)',
    backgroundSize: '200% 200%',
    animation: 'gradientAnimation 15s ease infinite',
  },
  contentCard: {
    padding: '32px 24px',
    borderRadius: 'var(--border-radius-lg)',
    boxShadow: 'var(--shadow-md)',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.8)',
    overflow: 'hidden',
    transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
    maxWidth: '800px',
    margin: '0 auto',
    '[data-theme="dark"] &': {
      backgroundColor: 'rgba(30, 41, 59, 0.8)',
      border: '1px solid rgba(30, 41, 59, 0.6)',
      boxShadow: 'var(--shadow-lg)',
    },
    '&:hover': {
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-5px)',
      '[data-theme="dark"] &': {
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
      },
    },
    '@media (max-width: 768px)': {
      padding: '24px 16px',
    },
  },
  appTitle: {
    background:
      'linear-gradient(45deg, var(--primary-color), var(--secondary-color))',
    backgroundClip: 'text',
    textFillColor: 'transparent',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontSize: '2.5rem',
    marginBottom: '8px',
    textAlign: 'center',
    fontWeight: 800,
    textShadow: '0 5px 15px rgba(67, 97, 238, 0.2)',
    letterSpacing: '-0.5px',
    '@media (max-width: 576px)': {
      fontSize: '1.75rem',
    },
  },
  appDescription: {
    color: 'var(--text-secondary)',
    maxWidth: '600px',
    margin: '0 auto 32px',
    textAlign: 'center',
    fontSize: '1.1rem',
    lineHeight: 1.6,
    '@media (max-width: 576px)': {
      fontSize: '1rem',
      marginBottom: '24px',
    },
  },
  tabsContainer: {
    marginTop: '24px',
  },
  tabsListWrapper: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginBottom: '32px',
  },
  tabsList: {
    width: 'auto',
    display: 'inline-flex',
    margin: '0 auto',
    border: '1px solid rgba(100, 116, 139, 0.1)',
    padding: '4px',
    borderRadius: 'var(--border-radius-full)',
    backgroundColor: 'rgba(241, 245, 249, 0.5)',
  },
  tab: {
    fontWeight: 600,
    height: '48px',
    padding: '0 24px',
    transition: 'var(--transition)',
    borderRadius: 'var(--border-radius-full)',
    color: 'var(--text-secondary)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      color: 'var(--primary-color)',
    },
    '&[data-active]': {
      backgroundColor: 'white',
      boxShadow: 'var(--shadow-sm)',
      color: 'var(--primary-color)',
    },
  },
  tabIcon: {
    marginRight: '8px',
  },
  tabPanel: {
    marginTop: '24px',
    animation: 'fadeIn 0.5s ease-out',
  },
  loadingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    flexDirection: 'column',
  },
  loadingText: {
    marginTop: '16px',
    color: 'var(--primary-color)',
    fontWeight: 500,
  },
  modelInfoBadge: {
    backgroundColor: 'rgba(46, 204, 113, 0.1)',
    color: '#2ecc71',
    padding: '10px 20px',
    borderRadius: 'var(--border-radius-full)',
    fontSize: '0.95rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    margin: '0 auto 28px',
    textAlign: 'center',
    width: 'fit-content',
    boxShadow: '0 2px 8px rgba(46, 204, 113, 0.15)',
    border: '1px solid rgba(46, 204, 113, 0.2)',
    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    transform: 'translateY(0)',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 4px 12px rgba(46, 204, 113, 0.2)',
    },
  },
}));

function App() {
  const { classes } = useStyles();
  const [activeTab, setActiveTab] = useState('webcam');
  const [imageData, setImageData] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isModelLoading, setIsModelLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [loadedModel, setLoadedModel] = useState(null);
  const [modelError, setModelError] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  const [colorScheme, setColorScheme] = useState(() => {
    // Get saved preference or use system preference
    const savedScheme = localStorage.getItem('color-scheme');
    if (savedScheme) {
      return savedScheme;
    }
    return window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  });

  // Apply dark mode class to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', colorScheme);
    localStorage.setItem('color-scheme', colorScheme);
  }, [colorScheme]);

  const toggleColorScheme = () => {
    setColorScheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllModels();
    };
  }, []);

  // Effect to load the model when selected
  useEffect(() => {
    if (selectedModel) {
      loadSelectedModel();
    }
  }, [selectedModel]);

  const loadSelectedModel = async () => {
    setIsModelLoading(true);
    setModelError(null);

    try {
      const model = await loadModel(selectedModel.path);
      setLoadedModel(model);
    } catch (error) {
      console.error('Failed to load model:', error);
      setModelError(`Failed to load model: ${error.message}`);
    } finally {
      setIsModelLoading(false);
    }
  };

  // Event handler for tab change
  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
  };

  // Handle model selection
  const handleModelSelect = (model) => {
    setSelectedModel(model);
  };

  // Function for prediction
  const predictWithModel = async (imageData) => {
    if (!loadedModel) {
      console.error('Model not loaded');
      return;
    }

    setIsLoading(true);

    try {
      const results = await predictAgeGender(loadedModel, imageData);
      setPredictions(results);
      setShowResults(true);
    } catch (error) {
      console.error('Prediction error:', error);
      // Show some error message to the user
      setPredictions({
        error: 'Failed to process image. Please try again.',
      });
      setShowResults(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageCapture = (imageSrc) => {
    setImageData(imageSrc);
    predictWithModel(imageSrc);
  };

  const handleImageUpload = (imageSrc) => {
    setImageData(imageSrc);
    predictWithModel(imageSrc);
  };

  const handleBack = () => {
    setShowResults(false);
    setPredictions(null);
    setImageData(null);
  };

  // If no model is selected, show the model selector
  if (!selectedModel) {
    return (
      <MantineProvider
        withGlobalStyles
        withNormalizeCSS
        theme={{ colorScheme }}
      >
        <Navbar
          onResetModelSelection={() => setSelectedModel(null)}
          colorScheme={colorScheme}
          toggleColorScheme={toggleColorScheme}
        />
        <div className={classes.mainContainer}>
          <Container size="lg">
            <ModelSelector onModelSelect={handleModelSelect} />

            {/*
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '20px',
                marginBottom: '40px',
              }}
            >
              <div 
                style={{
                  background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.05), rgba(76, 201, 240, 0.1))',
                  backdropFilter: 'blur(10px)',
                  padding: '10px 18px',
                  borderRadius: 'var(--border-radius-md)',
                  boxShadow: 'var(--shadow-sm)',
                  border: '1px solid rgba(255, 255, 255, 0.7)',
                  transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transform: 'translateY(0)',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '15px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                }}
              >
                <span 
                  style={{
                    color: 'var(--text-primary)',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    letterSpacing: '0.01em',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Debug Mode
                </span>
                <Switch
                  checked={debugMode}
                  onChange={(event) => setDebugMode(event.currentTarget.checked)}
                  color="blue"
                  size="md"
                  styles={{
                    root: {
                      margin: 0,
                      padding: 0,
                    },
                    track: {
                      backgroundColor: 'rgba(226, 232, 240, 0.8)',
                      borderRadius: '50px',
                      height: '24px',
                      width: '44px',
                    },
                    thumb: {
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      height: '20px',
                      width: '20px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    },
                    input: {
                      '&:checked': {
                        '& + .mantine-Switch-track': {
                          backgroundColor: 'var(--primary-color)',
                        },
                        '& + .mantine-Switch-track .mantine-Switch-thumb': {
                          transform: 'translateX(20px)',
                        },
                      },
                    },
                  }}
                  thumbIcon={debugMode ? <IconBug size={12} stroke={2.5} style={{ color: 'var(--primary-color)' }} /> : null}
                />
              </div>
            </div>
            */}

            {debugMode && (
              <div style={{ marginTop: '20px', marginBottom: '40px' }}>
                <Title
                  order={3}
                  style={{ marginBottom: '20px', textAlign: 'center' }}
                >
                  Debug: Test Model Loading
                </Title>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '20px',
                  }}
                >
                  <TestModel modelPath="resnet34/tfjs_model_float32" />
                  <TestModel modelPath="resnet34/tfjs_model_float16" />
                </div>
              </div>
            )}
          </Container>
        </div>
      </MantineProvider>
    );
  }

  return (
    <MantineProvider withGlobalStyles withNormalizeCSS theme={{ colorScheme }}>
      <Navbar
        onResetModelSelection={() => setSelectedModel(null)}
        colorScheme={colorScheme}
        toggleColorScheme={toggleColorScheme}
      />
      <div className={classes.mainContainer}>
        {(isLoading || isModelLoading) && (
          <div className={classes.loadingOverlay}>
            <Loader size="lg" variant="dots" color="blue" />
            <Text className={classes.loadingText}>
              {isModelLoading ? 'Loading model...' : 'Analyzing image...'}
            </Text>
          </div>
        )}
        <Container size="lg">
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              marginBottom: '20px',
            }}
          >
            <Button
              variant="filled"
              size="md"
              onClick={() => setSelectedModel(null)}
              leftSection={<IconArrowBack size={20} stroke={1.5} />}
              styles={{
                root: {
                  background:
                    'linear-gradient(135deg, rgba(67, 97, 238, 0.05), rgba(76, 201, 240, 0.1))',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(123, 125, 245, 0.7)',
                  boxShadow: 'var(--shadow-sm)',
                  borderRadius: '50px',
                  fontWeight: 600,
                  padding: '10px 24px',
                  height: 'auto',
                  transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transform: 'translateY(0)',
                  color: 'var(--primary-color)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background:
                      'linear-gradient(45deg, rgba(255,255,255,0.1), rgba(255,255,255,0))',
                    transform: 'translateX(-100%)',
                    transition: 'transform 0.6s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 20px rgba(67, 97, 238, 0.15)',
                    color: 'var(--primary-hover)',
                    '&::before': {
                      transform: 'translateX(100%)',
                    },
                  },
                },
                label: {
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '0.95rem',
                  letterSpacing: '0.03em',
                },
                leftSection: {
                  marginRight: '8px',
                },
              }}
            >
              Back to Model Selection
            </Button>
          </div>

          {!showResults ? (
            <Stack spacing="xl" align="center" className="animate-fadeIn">
              <Title className={classes.appTitle}>
                Face Age & Gender Detector
              </Title>

              <Text className={classes.appDescription}>
                Upload a photo or use your webcam to detect the age and gender
                of faces in the image. Our AI-powered system provides accurate
                predictions in seconds.
              </Text>

              <div
                className={classes.modelInfoBadge}
                style={{
                  display: 'block',
                  width: 'fit-content',
                  margin: '0 auto 24px auto',
                }}
              >
                {selectedModel.name} • {selectedModel.precision}
              </div>

              {modelError ? (
                <Paper shadow="sm" radius="md" className={classes.contentCard}>
                  <Stack align="center" spacing="md">
                    <Text color="red" size="lg" weight={500}>
                      {modelError}
                    </Text>
                    <Text>
                      There was an error loading the model. Please go back and
                      try selecting a different model.
                    </Text>
                    <button
                      onClick={() => setSelectedModel(null)}
                      className="btn-primary"
                      style={{ marginTop: '20px' }}
                    >
                      Back to Model Selection
                    </button>
                  </Stack>
                </Paper>
              ) : (
                <Paper shadow="sm" radius="md" className={classes.contentCard}>
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    color="blue"
                    variant="pills"
                    radius="md"
                    classNames={{
                      root: classes.tabsContainer,
                      tab: classes.tab,
                      panel: classes.tabPanel,
                    }}
                  >
                    <div className={classes.tabsListWrapper}>
                      <Tabs.List className={classes.tabsList}>
                        <Tabs.Tab
                          value="webcam"
                          leftSection={
                            <IconCamera className={classes.tabIcon} />
                          }
                        >
                          Use Webcam
                        </Tabs.Tab>
                        <Tabs.Tab
                          value="upload"
                          leftSection={
                            <IconUpload className={classes.tabIcon} />
                          }
                        >
                          Upload Photo
                        </Tabs.Tab>
                      </Tabs.List>
                    </div>

                    <Tabs.Panel value="webcam">
                      <WebcamCapture onImageCapture={handleImageCapture} />
                    </Tabs.Panel>

                    <Tabs.Panel value="upload">
                      <ImageUpload onImageUpload={handleImageUpload} />
                    </Tabs.Panel>
                  </Tabs>
                </Paper>
              )}
            </Stack>
          ) : (
            <>
              <ResultDisplay
                image={imageData}
                predictions={predictions}
                onBack={handleBack}
                modelInfo={selectedModel}
              />

              {debugMode && (
                <Paper
                  shadow="sm"
                  style={{
                    marginTop: '40px',
                    marginBottom: '30px',
                    padding: '20px',
                  }}
                >
                  <Title order={4} style={{ marginBottom: '15px' }}>
                    <IconBug size={18} style={{ marginRight: '8px' }} />
                    Prediction Debug Information
                  </Title>
                  <Text>Raw prediction data:</Text>
                  <pre
                    style={{
                      background: '#f5f5f5',
                      padding: '10px',
                      borderRadius: '4px',
                      overflow: 'auto',
                    }}
                  >
                    {JSON.stringify(predictions, null, 2)}
                  </pre>

                  {predictions && predictions.raw && (
                    <>
                      <Text
                        size="lg"
                        weight={500}
                        style={{ marginTop: '20px', marginBottom: '10px' }}
                      >
                        Verification Data:
                      </Text>
                      <ul
                        style={{
                          listStyle: 'none',
                          padding: '10px',
                          background: '#f0f8ff',
                          borderRadius: '4px',
                        }}
                      >
                        <li>
                          <strong>Raw Age Value:</strong>{' '}
                          {predictions.raw.ageValue.toFixed(4)} → Scaled:{' '}
                          {predictions.age}
                        </li>
                        {predictions.raw.genderProbs && (
                          <li>
                            <strong>Gender Probabilities:</strong>
                            <br />
                            Male:{' '}
                            {(predictions.raw.genderProbs[0] * 100).toFixed(2)}%
                            <br />
                            Female:{' '}
                            {(predictions.raw.genderProbs[1] * 100).toFixed(2)}%
                          </li>
                        )}
                        <li>
                          <strong>Selected Gender:</strong> {predictions.gender}
                        </li>
                      </ul>
                      <Text
                        size="sm"
                        style={{ marginTop: '10px', color: '#666' }}
                      >
                        These values should vary with different inputs if the
                        model is working correctly.
                      </Text>
                    </>
                  )}
                </Paper>
              )}
            </>
          )}
        </Container>
      </div>
    </MantineProvider>
  );
}

export default App;
